/**
 * Validation, suggestions, versions, and audit panels for protocol detail.
 */

import type {
  ProtocolAuditEvent,
  ProtocolConnection,
  ProtocolVersion,
} from "../../api/clinicalTrialsApiTypes";

export { ProtocolValidationPanel } from "./ProtocolValidationPanel";

type SuggestionsPanelProps = {
  busy: boolean;
  suggestions: ProtocolConnection[];
  onAccept: (connection: ProtocolConnection) => void;
};

/**
 * Renders deterministic missing-connection suggestions.
 *
 * @param props - Suggestions panel props
 * @returns Suggestions panel element
 */
export function ProtocolSuggestionsPanel({
  busy,
  suggestions,
  onAccept,
}: SuggestionsPanelProps) {
  if (suggestions.length === 0) return null;
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="mb-2 text-sm font-semibold">
        Connection suggestions ({suggestions.length})
      </h3>
      <ul className="space-y-2 text-sm">
        {suggestions.slice(0, 20).map((connection) => (
          <li
            key={connection.connection_id}
            className="flex items-center justify-between gap-3 rounded border border-slate-100 px-3 py-2"
          >
            <span className="font-mono text-xs">
              {connection.connection_type}: {connection.from_node_id} →{" "}
              {connection.to_node_id}
            </span>
            <button
              className="text-xs text-slate-900 underline"
              disabled={busy}
              onClick={() => void onAccept(connection)}
            >
              Accept
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

type HistoryPanelsProps = {
  versions: ProtocolVersion[];
  audit: ProtocolAuditEvent[];
};

/**
 * Renders published versions and audit history side by side.
 *
 * @param props - History panel props
 * @returns History panels element
 */
export function ProtocolHistoryPanels({ versions, audit }: HistoryPanelsProps) {
  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold">Published versions</h3>
        <ul className="space-y-2 text-sm">
          {versions.map((version) => (
            <li key={version.version_id} className="rounded border px-3 py-2">
              <div className="font-mono text-xs">{version.version_id}</div>
              <div className="text-xs text-slate-600">
                {version.source_protocol_version} · {version.published_at}
              </div>
              <div className="truncate text-xs text-slate-500">
                {version.definition_fingerprint}
              </div>
            </li>
          ))}
          {versions.length === 0 ? (
            <li className="text-xs text-slate-500">No published versions.</li>
          ) : null}
        </ul>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold">Audit</h3>
        <ul className="max-h-80 space-y-2 overflow-auto text-sm">
          {audit.map((event) => (
            <li key={event.event_id} className="rounded border px-3 py-2">
              <div className="font-medium">{event.event_type}</div>
              <div className="text-xs text-slate-500">
                {event.timestamp} · {event.actor_id}
              </div>
            </li>
          ))}
          {audit.length === 0 ? (
            <li className="text-xs text-slate-500">No audit events yet.</li>
          ) : null}
        </ul>
      </div>
    </section>
  );
}
